import { useReactiveVar } from '@apollo/client/react/hooks/useReactiveVar';
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Packages
 */
import { useQueryParams } from '@astrumu/ui-library';

/**
 * Types
 */
import { PredictionFilters } from '@/lib/types';

/**
 * Queries and Variables
 */
import { isLoggedInVar } from '@/apollo/cache/variables';
import { useAppConfigContext } from '@/providers/AppConfig';

type QueryParams = {
  purposeId: string;
  subTrackId: string;
  jobRoleId?: string;
  currentLocationId?: string;
  destinationLocationId?: string;
  years?: string;
  isRelocating?: string;
  majorId?: string;
};

type UsePredictionFilters = {
  predictionFilters: PredictionFilters;
  setPredictionFilters: (predictionFilters: PredictionFilters) => void;
};

const usePredictionFilters = (): UsePredictionFilters => {
  const navigate = useNavigate();
  const isLoggedIn = useReactiveVar(isLoggedInVar);
  const [searchParams] = useSearchParams();

  const {
    programType,
    sections: {
      introduction: {
        dataValues: { initial }
      }
    }
  } = useAppConfigContext();

  const queryParams = useQueryParams<QueryParams>();

  const programTypeName = programType.toLowerCase();

  const predictionFilters = useMemo(() => {
    //if we've saved query params from user before they were authorized- use them here
    const { purposeId: queryPurpose, subTrackId: querySubTrack } = queryParams;

    const {
      major,
      years,
      purpose,
      jobRole,
      subTrack,
      currentLocation,
      destinationLocation
    } = initial;

    const {
      jobRoleId: queryJobRole,
      currentLocationId: queryCurrentLocation,
      destinationLocationId: queryDestinationLocation,
      years: queryYears,
      isRelocating: queryIsRelocating,
      majorId: queryMajor
    } = isLoggedIn ? queryParams : ({} as QueryParams);

    // TODO: use IDs instead of names when API implemented
    const commonFields: PredictionFilters = {
      years: Number(queryYears) || years,
      purposeId: queryPurpose || purpose.id,
      subTrackId: querySubTrack || subTrack.id,
      jobRoleId: queryJobRole || jobRole.id,
      currentLocationId: queryCurrentLocation || currentLocation.id,
      destinationLocationId: queryDestinationLocation || destinationLocation.id,
      majorId: programTypeName !== 'mba' ? queryMajor || major.id : undefined,
      isRelocating: queryIsRelocating ? queryIsRelocating === 'true' : true
    };

    return commonFields;
  }, [queryParams, isLoggedIn, initial]);

  const setPredictionFilters = useCallback(
    (predictionFilters: PredictionFilters) => {
      const { purposeId, subTrackId } = predictionFilters;

      searchParams.set('purposeId', purposeId);
      searchParams.set('subTrackId', subTrackId);

      const loggedInParams: Array<
        keyof Omit<PredictionFilters, 'purposeId' | 'subTrackId'>
      > = [
        'years',
        'majorId',
        'jobRoleId',
        'isRelocating',
        'currentLocationId',
        'destinationLocationId'
      ];

      if (isLoggedIn) {
        loggedInParams.forEach(el => {
          if (predictionFilters[el]) {
            searchParams.set(el, String(predictionFilters[el]));
          } else {
            searchParams.delete(el);
          }
        });
      } else {
        loggedInParams.forEach(el => searchParams.delete(el));
      }

      navigate({
        search: searchParams.toString()
      });
    },
    [isLoggedIn, navigate, searchParams]
  );

  return {
    predictionFilters,
    setPredictionFilters
  };
};

export default usePredictionFilters;
